/**
 * /integracoes — Marketplace de integracoes do PharuERP (ADR-0035).
 *
 * Lista todas as integracoes disponiveis agrupadas por dominio com:
 *   - Pill de status (Ativa / Disponivel / Planejada / Em desenvolvimento)
 *   - Capabilities chips
 *   - Link "Configurar" -> tela proprietaria do dominio
 *   - Link docs externo
 *   - Custo estimado
 *
 * Read-only: marketplace nao expoe credenciais nem configura — apenas
 * indica o que existe + manda pra tela proprietaria.
 *
 * Permission: qualquer usuario autenticado.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiInfo,
  FiLink,
  FiSettings,
} from "react-icons/fi";
import styled from "styled-components";

import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { PageContainer } from "../../styles/shared";
import { logoIntegracao } from "../../assets/integracoes";

import { listarIntegracoes, STATUS_LABEL } from "../../services/integracoes";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  FilterBar,
  Pill,
  SectionTitle,
  Select,
} from "../Orcamentos/styles";


const STATUSES = [
  { value: "", label: "Todos os status" },
  { value: "ATIVA", label: "Ativas (em uso)" },
  { value: "DISPONIVEL", label: "Disponiveis (basta configurar)" },
  { value: "PLACEHOLDER", label: "Planejadas (futuro)" },
  { value: "EM_DESENVOLVIMENTO", label: "Em desenvolvimento" },
];


export default function MarketplaceIntegracoes() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroDominio, setFiltroDominio] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarIntegracoes(request, {
        dominio: filtroDominio,
        status: filtroStatus,
      });
      setData(p);
    } catch (err) {
      setError(err?.message || "Falha ao carregar integrações.");
    } finally {
      setLoading(false);
    }
  }, [request, filtroDominio, filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  const dominiosConhecidos = data?.dominios_conhecidos || [];
  const dominiosAtivos = data?.dominios || [];
  const integracoes = data?.integracoes || {};
  const total = data?.total || 0;

  // Opcoes do filtro de dominio: apenas dominios com pelo menos 1 integracao
  const opcoesDominio = [
    { value: "", label: "Todos os dominios" },
    ...dominiosConhecidos
      .filter((d) => dominiosAtivos.includes(d.slug))
      .map((d) => ({ value: d.slug, label: d.label })),
  ];

  return (
    <PageContainer>
      <EntityHeader
        title="Marketplace de Integrações"
        subtitle={`${total} integracao(oes) no catalogo do PharuERP`}
        icone={FiLink}
      />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <FilterBar>
          <Select
            value={filtroDominio}
            onChange={(e) => setFiltroDominio(e.target.value)}
          >
            {opcoesDominio.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
          <Select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
        </FilterBar>
      </Card>

      {error && <ErrorMsg>{error}</ErrorMsg>}
      {loading && <EmptyMsg>Carregando integrações...</EmptyMsg>}

      {!loading && total === 0 && (
        <EmptyMsg>Nenhuma integração encontrada com esses filtros.</EmptyMsg>
      )}

      {!loading && total > 0 && (
        <DominiosWrap>
          {dominiosConhecidos
            .filter((d) => integracoes[d.slug]?.length > 0)
            .map((d) => (
              <DominioCard
                key={d.slug}
                dominio={d}
                integracoes={integracoes[d.slug] || []}
                navigate={navigate}
              />
            ))}
        </DominiosWrap>
      )}
    </PageContainer>
  );
}


function DominioCard({ dominio, integracoes, navigate }) {
  return (
    <Card>
      <DominioHeader>
        <SectionTitle>{dominio.label}</SectionTitle>
        <DominioCount>{integracoes.length} integracao(oes)</DominioCount>
      </DominioHeader>

      <IntegracoesGrid>
        {integracoes.map((m) => (
          <IntegracaoCard key={`${m.dominio}:${m.slug}`} metadata={m} navigate={navigate} />
        ))}
      </IntegracoesGrid>
    </Card>
  );
}


function IntegracaoCard({ metadata, navigate }) {
  const m = metadata;
  return (
    <ProviderCard>
      <ProviderCardHeader>
        <ProviderTitleGroup>
          <ProviderLogo slug={m.slug} nome={m.nome} />
          <strong>{m.nome}</strong>
        </ProviderTitleGroup>
        <Pill $tone={m.status_tone || "neutral"}>
          {iconeStatus(m.status)} {STATUS_LABEL[m.status] || m.status}
        </Pill>
      </ProviderCardHeader>

      <ProviderDescricao>{m.descricao}</ProviderDescricao>

      {m.capabilities?.length > 0 && (
        <CapabilitiesRow>
          {m.capabilities.map((cap) => (
            <CapChip key={cap}>{cap.replace(/_/g, " ")}</CapChip>
          ))}
        </CapabilitiesRow>
      )}

      {m.custo_estimado && (
        <ProviderMeta><strong>Custo:</strong> {m.custo_estimado}</ProviderMeta>
      )}

      <ProviderActions>
        {m.requer_configuracao && m.link_configuracao && (
          <ActionBtn
            onClick={() => navigate(m.link_configuracao)}
            disabled={m.status === "PLACEHOLDER" || m.status === "EM_DESENVOLVIMENTO"}
            title={
              m.status === "PLACEHOLDER"
                ? "Integração planejada — ainda não implementada"
                : ""
            }
          >
            <FiSettings size={14} /> Configurar
          </ActionBtn>
        )}
        {m.documentacao_url && (
          <ActionBtnSecondary
            href={m.documentacao_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiExternalLink size={14} /> Documentacao
          </ActionBtnSecondary>
        )}
      </ProviderActions>
    </ProviderCard>
  );
}


// Logo real da marca (asset local). Cai num monograma neutro quando não há
// logo (ex.: "Mock") ou se o arquivo falhar ao carregar.
function ProviderLogo({ slug, nome }) {
  const src = logoIntegracao(slug);
  const [erro, setErro] = useState(false);
  const mono = (nome || "?").trim().charAt(0).toUpperCase();
  if (!src || erro) {
    return <LogoBox className="mono" aria-hidden="true">{mono}</LogoBox>;
  }
  return (
    <LogoBox>
      <img src={src} alt="" onError={() => setErro(true)} />
    </LogoBox>
  );
}


function iconeStatus(status) {
  switch (status) {
    case "ATIVA":
      return <FiCheckCircle size={11} />;
    case "DISPONIVEL":
      return <FiInfo size={11} />;
    case "PLACEHOLDER":
      return <FiClock size={11} />;
    case "EM_DESENVOLVIMENTO":
      return <FiAlertCircle size={11} />;
    default:
      return null;
  }
}


// ============================================================
// Styles locais
// ============================================================

const DominiosWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DominioHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DominioCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
`;

const IntegracoesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
`;

const ProviderCard = styled.div`
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProviderCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  strong {
    font-size: 0.95rem;
    color: ${({ theme }) => theme.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ProviderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const LogoBox = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 7px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.85rem;
  font-weight: 600;

  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }
`;

const ProviderDescricao = styled.p`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.4;
  margin: 0;
`;

const CapabilitiesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const CapChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.7rem;
  border-radius: 6px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textSecondary};
`;

const ProviderMeta = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 4px;
`;

const ProviderActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  font-size: 0.78rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.accent};
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.accentText || "#fff"};
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.88;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ActionBtnSecondary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  font-size: 0.78rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;
